import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { space } from 'styled-system';

import { Link } from 'common/components/Link';
import type { CommonTypographyProps } from 'common/components/Typography';
import { HeadlineH8, LinkRegularLR1 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import { useHelpLinks } from '../../../hooks/help-links';
import { helpLinkTrackingEvent } from '../../../utils/tracking';

interface HelpSectionLinksProps {
  reason: string;
}

const UnstyledContainer = styled.div<SpaceProps>`
  ${space};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer mb="sp_20" {...props} />
);

const titleStyles: CommonTypographyProps = {
  pt: ['sp_4', 'sp_0'],
  mb: ['sp_8', null, null, 'sp_12'],
};

const UnstyledList = styled.ul<SpaceProps>`
  ${space};
`;

const List = (props: ComponentProps<typeof UnstyledList>) => (
  <UnstyledList pl="sp_16" m="sp_0" {...props} />
);

const UnstyledListItem = styled.li<SpaceProps>`
  ${space};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const ListItem = (props: ComponentProps<typeof UnstyledListItem>) => (
  <UnstyledListItem mb="sp_8" {...props} />
);

const UnstyledStyledLink = styled(LinkRegularLR1)<ComponentProps<typeof Link>>``;

const StyledLink = (props: ComponentProps<typeof UnstyledStyledLink>) => (
  <UnstyledStyledLink as={Link} textDecoration="underline" rel="noopener noreferrer" {...props} />
);

const HelpSectionLinks = ({ reason }: HelpSectionLinksProps) => {
  const { t } = useLocalization();
  const helpLinks = useHelpLinks(reason);

  if (helpLinks.length === 0) {
    return null;
  }

  return (
    <Container data-testid="help-section-container">
      <HeadlineH8 {...titleStyles}>{t('h24_contact_form_maybe_this_help')}</HeadlineH8>
      <List>
        {helpLinks.map(({ label, href }) => (
          <ListItem key={label}>
            <StyledLink
              href={href}
              onClick={() => helpLinkTrackingEvent(reason, label)}
              target="_blank"
            >
              {label}
            </StyledLink>
          </ListItem>
        ))}
      </List>
    </Container>
  );
};

export default HelpSectionLinks;
